import { isEthiopianLeapYear, toEthiopian, toGregorian } from 'ethiopian-calendar-new';
import Holiday from '@/services/holiday';
import { HOLIDAYS_ROUTE, LEAVE_TYPES_ROUTE, LEAVES_ROUTE } from '@/router/routes';
import { fromDateKey, toDateKey } from '@/utils/attendance_dates';
import { MONTHS_ETHIOPIAN } from '@/utils/const';

/* ----------------------------------- hub ----------------------------------- */

export const LEAVE_TABS = [
  { key: 'requests', label: 'Requests', icon: 'pi-inbox', route: LEAVES_ROUTE },
  { key: 'holidays', label: 'Holidays', icon: 'pi-sun', route: HOLIDAYS_ROUTE },
  { key: 'types', label: 'Leave types', icon: 'pi-sliders-h', route: LEAVE_TYPES_ROUTE },
];

/* ---------------------------------- dates ---------------------------------- */

/** "2026-09-13T00:00:00.000Z" or a Date-like string -> "2026-09-13". */
export const dateKeyOf = (value: any): string => (value ? String(value).slice(0, 10) : '');

export const todayKey = (): string => toDateKey(new Date());

const shiftDays = (date: Date, days: number): Date => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + days);
  return d;
};

export const addDaysToKey = (key: string, days: number): string => toDateKey(shiftDays(fromDateKey(key), days));

export const formatDays = (value: any): string => {
  const n = Number(value) || 0;
  return `${Number.isInteger(n) ? n : n.toFixed(1)} day${n === 1 ? '' : 's'}`;
};

export interface DayRules {
  holidays: Set<string>;
  skip: Set<string>;
  include: Set<string>;
}

/** A day counts towards leave unless it is skipped, or is a holiday the request does not count. */
export const countsAsLeave = (key: string, rules: DayRules): boolean =>
  !rules.skip.has(key) && (!rules.holidays.has(key) || rules.include.has(key));

/** Last day of a request. Mirrors the server: walk forward from the start until enough days count. */
export const leaveEndDate = (startKey: string, days: any, rules: DayRules): string => {
  if (!startKey) return '';
  const target = Math.max(1, Math.ceil(Number(days) || 0));
  let current = fromDateKey(startKey);
  let counted = 0;
  for (let guard = 0; guard < 1000; guard += 1) {
    if (countsAsLeave(toDateKey(current), rules)) counted += 1;
    if (counted >= target) break;
    current = shiftDays(current, 1);
  }
  return toDateKey(current);
};

export interface CalendarCell {
  key: string;
  day: number;
  inMonth: boolean;
}

export interface MonthView {
  label: string;
  first: Date;
  last: Date;
  cells: CalendarCell[];
}

const ethiopicDay = (date: Date): number => toEthiopian(date.getFullYear(), date.getMonth() + 1, date.getDate()).day;

/** Sunday-first month grid in the organization's calendar, `offset` months away from the month holding `anchorKey`. */
export const monthView = (anchorKey: string, offset: number, ethiopic: boolean): MonthView => {
  const anchor = anchorKey ? fromDateKey(anchorKey) : new Date();
  let first: Date;
  let last: Date;
  let label: string;
  let dayOf: (date: Date) => number;

  if (ethiopic) {
    const eth = toEthiopian(anchor.getFullYear(), anchor.getMonth() + 1, anchor.getDate());
    let month = eth.month + offset;
    let year = eth.year;
    while (month > 13) {
      month -= 13;
      year += 1;
    }
    while (month < 1) {
      month += 13;
      year -= 1;
    }
    const length = month <= 12 ? 30 : isEthiopianLeapYear(year) ? 6 : 5;
    const start = toGregorian(year, month, 1);
    first = new Date(start.year, start.month - 1, start.day);
    last = shiftDays(first, length - 1);
    label = `${MONTHS_ETHIOPIAN[month - 1]} ${year}`;
    dayOf = ethiopicDay;
  } else {
    first = new Date(anchor.getFullYear(), anchor.getMonth() + offset, 1);
    last = new Date(first.getFullYear(), first.getMonth() + 1, 0);
    label = first.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    dayOf = (date) => date.getDate();
  }

  const firstKey = toDateKey(first);
  const lastKey = toDateKey(last);
  const gridEnd = shiftDays(last, 6 - last.getDay());
  const cells: CalendarCell[] = [];
  for (let d = shiftDays(first, -first.getDay()); d <= gridEnd; d = shiftDays(d, 1)) {
    const key = toDateKey(d);
    cells.push({ key, day: dayOf(d), inMonth: key >= firstKey && key <= lastKey });
  }
  return { label, first, last, cells };
};

/* --------------------------------- holidays -------------------------------- */

export interface HolidayEvent {
  id: string;
  title: string;
  start: string;
  end?: string;
  type: string;
}

/**
 * Holidays between two dates. The endpoint answers one Gregorian month at a
 * time, so a range that crosses months (every Ethiopian month does) is fetched
 * month by month and merged.
 */
export const fetchHolidaysBetween = async (organizationId: string, from: Date, to: Date): Promise<HolidayEvent[]> => {
  if (!organizationId) return [];
  const months: string[] = [];
  const cursor = new Date(from.getFullYear(), from.getMonth(), 1);
  const stop = new Date(to.getFullYear(), to.getMonth(), 1);
  while (cursor <= stop && months.length < 24) {
    months.push(`${toDateKey(cursor).slice(0, 7)}-15`);
    cursor.setMonth(cursor.getMonth() + 1);
  }

  const responses = await Promise.all(
    months.map((calendarDate) => Holiday.getCalendarHolidays({ organizationId, calendarDate } as any).catch(() => null))
  );

  const fromKey = toDateKey(from);
  const toKey = toDateKey(to);
  const seen = new Set<string>();
  const events: HolidayEvent[] = [];
  responses.forEach((res: any) => {
    const list = res?.data?.status && Array.isArray(res.data.data) ? res.data.data : [];
    list.forEach((item: any) => {
      const start = dateKeyOf(item.start);
      const key = `${item.id}|${start}`;
      if (!start || start < fromKey || start > toKey || seen.has(key)) return;
      seen.add(key);
      events.push({ ...item, start, end: start });
    });
  });
  return events.sort((a, b) => a.start.localeCompare(b.start) || String(a.title).localeCompare(String(b.title)));
};

export const HOLIDAY_TYPES: Record<string, { label: string; short: string; pill: string; bg: string; text: string }> = {
  calendar: { label: 'Public holiday', short: 'Public', pill: 'status-holiday', bg: '#f5f3ff', text: '#6d28d9' },
  organization: { label: 'Company holiday', short: 'Company', pill: 'status-present', bg: '#e8f4e4', text: '#205410' },
  weekly: { label: 'Weekly day off', short: 'Weekly', pill: 'status-off', bg: '#f3f4f6', text: '#3f3f46' },
};

export const holidayTypeMeta = (type: string) => HOLIDAY_TYPES[type] ?? HOLIDAY_TYPES.organization;

/** Calendar events carry weekly occurrences as "<holidayId>-!<date>". */
export const holidayIdOf = (event: any): string => String(event?.id ?? '').split('-!')[0];

export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/* --------------------------------- requests -------------------------------- */

export const requestStart = (request: any): string => dateKeyOf(request?.startDate);

export const requestEnd = (request: any): string => dateKeyOf(request?.endDate) || requestStart(request);

export const isOnLeaveToday = (request: any): boolean => {
  const today = todayKey();
  return request?.status === 'approved' && requestStart(request) <= today && requestEnd(request) >= today;
};

export const startsWithinDays = (request: any, days: number): boolean => {
  const today = todayKey();
  const start = requestStart(request);
  return request?.status === 'approved' && start > today && start <= addDaysToKey(today, days);
};

/** Balance row for the request's leave type, joined onto the employee by the requests endpoint. */
export const requestBalance = (request: any): any => {
  const balances = request?.employee?.leaveBalances ?? [];
  return balances.find((b: any) => b.leaveTypeId === request?.leaveTypeId) ?? null;
};

/* ---------------------------------- policy --------------------------------- */

export const ACCRUAL_OPTIONS = [
  { value: 'annual', label: 'Yearly grant', hint: 'The full allowance lands at the start of each leave year.' },
  { value: 'monthly', label: 'Monthly accrual', hint: 'The yearly allowance builds up a little each month.' },
  { value: 'none', label: 'Per occurrence', hint: 'Given each time the leave is needed, such as maternity or bereavement.' },
];

export const GENDER_OPTIONS = [
  { value: 'any', label: 'Everyone' },
  { value: 'female', label: 'Women' },
  { value: 'male', label: 'Men' },
];

export const PRORATION_OPTIONS = [
  { value: 'monthly', label: 'By month' },
  { value: 'daily', label: 'By day' },
];

export const emptyLeavePolicy = () => ({
  accrualMethod: 'annual',
  baseDays: null as number | null,
  incrementEveryYears: null as number | null,
  incrementByDays: null as number | null,
  prorationEnabled: false,
  prorationMethod: 'monthly',
  balanceTracked: true,
  capPerYearDays: null as number | null,
  capPerYearOccurrences: null as number | null,
  carryoverEnabled: false,
  carryoverMaxDays: null as number | null,
  carryoverMaxYears: null as number | null,
  evidenceRequiresCertificate: false,
  evidenceNoteLabel: null as string | null,
  eligibilityGender: 'any',
  eligibilityMinServiceMonths: 0,
});

const num = (value: any): number | null => (value === null || value === undefined || value === '' || Number.isNaN(Number(value)) ? null : Number(value));

const count = (n: number, word: string): string => `${Number.isInteger(n) ? n : n.toFixed(1)} ${word}${n === 1 ? '' : 's'}`;

export interface PolicyLine {
  icon: string;
  text: string;
}

/** The rules of a leave policy as short sentences, most important first. */
export const leavePolicyLines = (policy: any): PolicyLine[] => {
  if (!policy) return [];
  const lines: PolicyLine[] = [];
  const method = String(policy.accrualMethod ?? 'none').toLowerCase();
  const base = num(policy.baseDays);
  const cap = num(policy.capPerYearDays);

  if (base && method === 'annual') lines.push({ icon: 'pi-calendar-plus', text: `${count(base, 'day')} granted each year` });
  else if (base && method === 'monthly') lines.push({ icon: 'pi-calendar-plus', text: `${count(base, 'day')} a year, accrued monthly` });
  else if (base) lines.push({ icon: 'pi-calendar-plus', text: `${count(base, 'day')} each time it is taken` });
  else if (cap) lines.push({ icon: 'pi-calendar-plus', text: `Up to ${count(cap, 'day')} a year` });
  else lines.push({ icon: 'pi-calendar-plus', text: 'No set allowance' });

  const every = num(policy.incrementEveryYears);
  const by = num(policy.incrementByDays);
  if (every && by) lines.push({ icon: 'pi-arrow-up-right', text: `${count(by, 'extra day')} every ${count(every, 'year')} of service` });

  if (cap && base) lines.push({ icon: 'pi-minus-circle', text: `No more than ${count(cap, 'day')} a year` });

  const times = num(policy.capPerYearOccurrences);
  if (times) lines.push({ icon: 'pi-replay', text: times === 1 ? 'Once a year at most' : `Up to ${times} times a year` });

  if (policy.carryoverEnabled) {
    const maxDays = num(policy.carryoverMaxDays);
    const maxYears = num(policy.carryoverMaxYears);
    lines.push({
      icon: 'pi-forward',
      text: `Unused days carry over${maxDays ? `, up to ${count(maxDays, 'day')}` : ''}${maxYears ? ` for ${count(maxYears, 'year')}` : ''}`,
    });
  }

  const gender = String(policy.eligibilityGender ?? 'any').toLowerCase();
  const months = num(policy.eligibilityMinServiceMonths);
  const who = [gender === 'female' ? 'women only' : gender === 'male' ? 'men only' : '', months ? `after ${count(months, 'month')} of service` : '']
    .filter(Boolean)
    .join(', ');
  if (who) lines.push({ icon: 'pi-user', text: who.charAt(0).toUpperCase() + who.slice(1) });

  if (policy.evidenceRequiresCertificate) {
    const label = String(policy.evidenceNoteLabel ?? '').trim();
    lines.push({ icon: 'pi-paperclip', text: label ? `Requires ${label.charAt(0).toLowerCase()}${label.slice(1)}` : 'Requires a certificate' });
  }

  return lines;
};
